import {
    resolveSiteNavigationItems,
    resolveSiteNavigationSettings,
    resolveSiteNavigationThemeTokens,
    siteNavigationAuthoredItems,
    siteNavigationThemeStyle,
    type SiteNavigationSettings,
    type SiteNavigationThemeStyle,
    type SiteNavigationVariant,
    type SiteNavigationViewport,
} from "./siteNavigation";
import type {
    SiteBuilderBlock,
    SiteBuilderDesign,
    SiteBuilderRuntimePageLink,
    SiteBuilderRuntimeSite,
    SiteNavigationItem,
    SiteNavigationMenu,
} from "./siteBuilderTypes";

const CLIENT_PORTAL_URL_TOKEN = "{{clientPortal.url}}";

const WEB_NAVIGATION_VARIANT_DEFAULTS: Readonly<Record<string, SiteNavigationVariant>> = {
    "atelier-muse": "editorialRule",
    "boutique-service": "minimalReveal",
    "craftsman-modern": "boldUnderline",
    "dental-studio": "minimalReveal",
    "groundwork-build": "boldUnderline",
    "kinetic-club": "neonLine",
    "launchpad-bold": "boldUnderline",
    "meridian-advisory": "editorialRule",
    "nocturne-club": "neonLine",
    "northline-stay": "minimalReveal",
    "restaurant-brunch": "playfulFrame",
    "restaurant-cafe-brunch": "editorialRule",
    "restaurant-modern": "minimalReveal",
    "restaurant-night-bar": "neonLine",
    "restaurant-pizzeria": "boldUnderline",
    "studio-clean": "minimalReveal",
    takeres: "playfulFrame",
    "torque-service": "boldUnderline",
    "volt-flow": "minimalReveal",
};

const WEB_CONTAINER_RESPONSIVE_NAVIGATION_TEMPLATES = new Set([
    "atelier-muse",
    "craftsman-modern",
    "dental-studio",
    "groundwork-build",
    "kinetic-club",
    "meridian-advisory",
    "nocturne-club",
    "restaurant-night-bar",
    "northline-stay",
    "studio-clean",
    "torque-service",
    "volt-flow",
]);

export type WebSiteNavigationLabels = Readonly<{
    clientPortal: string;
    closeMenu: string;
    closeSubmenu: string;
    navigation: string;
    openMenu: string;
    openSubmenu: string;
    recommended: string;
    showAll: string;
}>;

export type WebSiteNavigationModel = Readonly<{
    activeItemUuid: string | null;
    items: readonly SiteNavigationItem[];
    labels: WebSiteNavigationLabels;
    settings: SiteNavigationSettings;
    style: SiteNavigationThemeStyle;
}>;

/**
 * Adapts legacy Web header text links to the neutral tree without changing
 * their authored labels, URL fragments, protocols or nesting order.
 */
export function webAuthoredNavigationItems(
    navigationBlock: SiteBuilderBlock | null | undefined,
): SiteNavigationItem[] {
    return siteNavigationAuthoredItems(navigationBlock);
}

export function webSiteNavigationDefaultVariant(
    templateCode: string | null | undefined,
): SiteNavigationVariant {
    return WEB_NAVIGATION_VARIANT_DEFAULTS[templateCode ?? ""]
        ?? "minimalReveal";
}

export function webSiteNavigationUsesContainerBreakpoint(
    templateCode: string | null | undefined,
): boolean {
    return WEB_CONTAINER_RESPONSIVE_NAVIGATION_TEMPLATES.has(templateCode ?? "");
}

export function webSiteNavigationLabels(
    languageCode: string | null | undefined,
): WebSiteNavigationLabels {
    const language = languageCode?.trim().toLowerCase().split(/[-_]/u)[0] ?? "";

    if (language === "sk") {
        return {
            clientPortal: "Klientska zóna",
            closeMenu: "Zavrieť hlavné menu",
            closeSubmenu: "Zavrieť podmenu",
            navigation: "Hlavná navigácia",
            openMenu: "Otvoriť hlavné menu",
            openSubmenu: "Otvoriť podmenu",
            recommended: "Odporúčame",
            showAll: "Zobraziť všetko",
        };
    }

    if (language === "cs" || language === "cz") {
        return {
            clientPortal: "Klientská zóna",
            closeMenu: "Zavřít hlavní menu",
            closeSubmenu: "Zavřít podnabídku",
            navigation: "Hlavní navigace",
            openMenu: "Otevřít hlavní menu",
            openSubmenu: "Otevřít podnabídku",
            recommended: "Doporučujeme",
            showAll: "Zobrazit vše",
        };
    }

    if (language === "de") {
        return {
            clientPortal: "Kundenbereich",
            closeMenu: "Hauptmenü schließen",
            closeSubmenu: "Untermenü schließen",
            navigation: "Hauptnavigation",
            openMenu: "Hauptmenü öffnen",
            openSubmenu: "Untermenü öffnen",
            recommended: "Empfohlen",
            showAll: "Alles anzeigen",
        };
    }

    if (language === "es") {
        return {
            clientPortal: "Área de clientes",
            closeMenu: "Cerrar el menú principal",
            closeSubmenu: "Cerrar submenú",
            navigation: "Navegación principal",
            openMenu: "Abrir el menú principal",
            openSubmenu: "Abrir submenú",
            recommended: "Recomendado",
            showAll: "Ver todo",
        };
    }

    if (language === "pl") {
        return {
            clientPortal: "Strefa klienta",
            closeMenu: "Zamknij menu główne",
            closeSubmenu: "Zamknij podmenu",
            navigation: "Główna nawigacja",
            openMenu: "Otwórz menu główne",
            openSubmenu: "Otwórz podmenu",
            recommended: "Polecane",
            showAll: "Zobacz wszystko",
        };
    }

    if (language === "hu") {
        return {
            clientPortal: "Ügyfélfelület",
            closeMenu: "Főmenü bezárása",
            closeSubmenu: "Almenü bezárása",
            navigation: "Fő navigáció",
            openMenu: "Főmenü megnyitása",
            openSubmenu: "Almenü megnyitása",
            recommended: "Ajánlott",
            showAll: "Összes megtekintése",
        };
    }

    if (language === "ro") {
        return {
            clientPortal: "Zona clientului",
            closeMenu: "Închide meniul principal",
            closeSubmenu: "Închide submeniul",
            navigation: "Navigare principală",
            openMenu: "Deschide meniul principal",
            openSubmenu: "Deschide submeniul",
            recommended: "Recomandate",
            showAll: "Vezi tot",
        };
    }

    return {
        clientPortal: "Client area",
        closeMenu: "Close main menu",
        closeSubmenu: "Close submenu",
        navigation: "Main navigation",
        openMenu: "Open main menu",
        openSubmenu: "Open submenu",
        recommended: "Recommended",
        showAll: "Show all",
    };
}

function isClientPortalNavigationItem(
    item: SiteNavigationItem,
    portalUrl: string | null,
): boolean {
    const href = item.href?.trim() ?? "";

    return item.type === "clientPortal"
        || item.type === "clientPortalButton"
        || href === CLIENT_PORTAL_URL_TOKEN
        || (portalUrl !== null && href.replace(/\/+$/u, "") === portalUrl.replace(/\/+$/u, ""));
}

/**
 * Exposes the connected Client Portal as a themed navigation action. The item
 * is runtime-owned, so connecting or disconnecting a portal updates every
 * template without creating a fake local page or mutating authored menus.
 * An authored portal target wins, preserving its custom label and position.
 */
export function withWebClientPortalNavigationItem(
    items: readonly SiteNavigationItem[],
    clientPortal: SiteBuilderRuntimeSite["clientPortal"],
    languageCode?: string | null,
): readonly SiteNavigationItem[] {
    const portalUrl = clientPortal?.url?.trim() || null;
    let foundAuthoredItem = false;

    const resolveItems = (
        candidates: readonly SiteNavigationItem[],
    ): SiteNavigationItem[] => candidates.flatMap((item) => {
        const matchesPortal = isClientPortalNavigationItem(item, portalUrl);

        if (matchesPortal && portalUrl === null) {
            return [];
        }

        const children = resolveItems(item.children);

        if (!matchesPortal) {
            return [{ ...item, children }];
        }

        foundAuthoredItem = true;

        return [{
            ...item,
            children,
            href: portalUrl,
            openInNewTab: false,
            type: "clientPortal",
        }];
    });
    const resolvedItems = resolveItems(items);

    if (portalUrl === null || foundAuthoredItem) {
        return resolvedItems;
    }

    return [
        ...resolvedItems,
        {
            children: [],
            href: portalUrl,
            label: webSiteNavigationLabels(languageCode).clientPortal,
            openInNewTab: false,
            type: "clientPortal",
            uuid: `client-portal:${clientPortal?.uuid ?? "connected"}`,
        },
    ];
}

function comparableHref(href: string | null | undefined): string | null {
    if (!href || href.startsWith("#")) {
        return null;
    }

    try {
        const url = new URL(href, "https://navigation.invalid");
        const pathname = url.pathname.replace(/\/+$/u, "") || "/";

        return `${pathname}${url.search}`;
    } catch {
        return href.split("#", 1)[0]?.replace(/\/+$/u, "") || null;
    }
}

function activeItemUuid(
    items: readonly SiteNavigationItem[],
    activeHref: string | null,
): string | null {
    for (const item of items) {
        if (activeHref !== null && comparableHref(item.href) === activeHref) {
            return item.uuid;
        }

        const childUuid = activeItemUuid(item.children, activeHref);

        if (childUuid !== null) {
            return childUuid;
        }
    }

    return null;
}

export function resolveWebSiteNavigationModel({
    block,
    clientPortal,
    currentPageUuid,
    design,
    languageCode,
    menus = [],
    pages,
    templateCode,
    viewport = "desktop",
}: {
    block: SiteBuilderBlock;
    clientPortal?: SiteBuilderRuntimeSite["clientPortal"];
    currentPageUuid?: string | null;
    design: SiteBuilderDesign;
    languageCode?: string | null;
    menus?: readonly SiteNavigationMenu[];
    pages: readonly SiteBuilderRuntimePageLink[];
    templateCode?: string | null;
    viewport?: SiteNavigationViewport;
}): WebSiteNavigationModel {
    const settings = resolveSiteNavigationSettings(block, {
        theme: "inherit",
        variant: webSiteNavigationDefaultVariant(templateCode),
    });
    const items = withWebClientPortalNavigationItem(
        resolveSiteNavigationItems({
            authoredItems: webAuthoredNavigationItems(block),
            languageCode,
            menus,
            pages,
            settings,
            viewport,
        }),
        clientPortal,
        languageCode,
    );
    const currentPageHref = pages.find((page) => (
        page.pageUuid === currentPageUuid || page.uuid === currentPageUuid
    ))?.href ?? null;

    return {
        activeItemUuid: activeItemUuid(items, comparableHref(currentPageHref)),
        items,
        labels: webSiteNavigationLabels(languageCode),
        settings,
        style: siteNavigationThemeStyle(resolveSiteNavigationThemeTokens(
            design,
            settings.theme,
        )),
    };
}
